/**
 * Convierte una pregunta en lenguaje natural (en inglés) en una consulta de Europe PMC.
 *
 * Europe PMC trata cada palabra como obligatoria, así que "Does milk improve bone
 * health?" exige que aparezcan "does", "improve" y "health". Nos quedamos con los
 * términos de contenido, cubrimos singular y plural, y los unimos con AND.
 */

const STOPWORDS = new Set([
  // «Is dark matter made of WIMPs?», «consist of…»: forma de la pregunta, no tema
  "made",
  "consist",
  "consists",
  "composed",
  // funcionales
  "a", "an", "the", "is", "are", "was", "were", "be", "been", "being", "am", "do", "does",
  "did", "doing", "have", "has", "had", "it", "its", "this", "that", "these", "those",
  "there", "their", "they", "them", "you", "your", "we", "our", "i", "me", "my", "he",
  "she", "his", "her", "of", "for", "to", "in", "on", "at", "by", "with", "without",
  "from", "into", "about", "as", "and", "or", "not", "no", "if", "than", "then", "so",
  "can", "could", "should", "would", "will", "may", "might", "must", "shall",
  "what", "which", "who", "whom", "whose", "why", "how", "when", "where", "whether",
  "any", "some", "all", "more", "most", "less", "very", "much", "many", "too", "also",
  "just", "only", "really", "actually", "truly", "indeed", "true", "false", "myth", "fact",
  // verbos y adjetivos valorativos: los abstracts usan otras palabras
  "good", "bad", "better", "worse", "best", "worst", "healthy", "unhealthy", "health",
  "beneficial", "harmful", "benefit", "benefits", "harm", "harms", "effect", "effects",
  "effective", "affect", "affects", "impact", "impacts", "improve", "improves",
  "improving", "help", "helps", "helping", "prevent", "prevents", "preventing", "cause",
  "causes", "causing", "reduce", "reduces", "reducing", "increase", "increases",
  "increasing", "lower", "lowers", "raise", "raises", "make", "makes", "work", "works",
  "consume", "consumes", "consuming", "consumption", "eat", "eats", "eating", "drink",
  "drinks", "drinking", "take", "takes", "taking", "use", "uses", "using", "get", "gets",
  "getting", "got", "lose", "losing", "gain", "gaining", "people", "person", "body", "safe",
  // cantidades vagas
  "little", "lot", "lots", "bit", "few", "enough", "often", "several", "certain",
  // tiempo y cambio: describen la pregunta, no el tema ("screens before bed worsen sleep")
  "before", "after", "during", "while", "worsen", "worsens", "worsening", "raising", "lowering",
  "boost", "boosts", "promote", "promotes", "linked", "link", "associated", "relationship",
]);
// Letras que forman parte del término ("vitamin C", "omega 3")
const KEEP_SUFFIX_AFTER = new Set(["vitamin", "omega", "type"]);
// Palabras terminadas en s que no son plurales
const NOT_PLURAL_ENDINGS = ["ss", "us", "is", "ous", "sis"];
const INVARIANT = new Set([
  "diabetes", "series", "species", "news", "measles", "mumps", "rabies", "herpes",
  "scabies", "rickets", "sars", "aids", "covid-19", "physics", "genetics", "economics",
]);
const IRREGULAR: Record<string, string> = {
  child: "children", woman: "women", man: "men", tooth: "teeth",
  foot: "feet", mouse: "mice", person: "people",
};
const IRREGULAR_PLURALS: Record<string, string> = Object.fromEntries(
  Object.entries(IRREGULAR).map(([singular, plural]) => [plural, singular]),
);

export class QueryError extends Error {}

/** Singular y plural aproximados (bone/bones, berry/berries, child/children). */
function variants(word: string): string[] {
  const plural = IRREGULAR[word];
  if (plural) return [word, plural];
  const singular = IRREGULAR_PLURALS[word];
  if (singular) return [singular, word];
  if (word.endsWith("ing")) return [word];
  if (INVARIANT.has(word) || NOT_PLURAL_ENDINGS.some((e) => word.endsWith(e)) || word.length <= 3) {
    return [word];
  }
  if (word.endsWith("ies")) return [word.slice(0, -3) + "y", word];
  if (word.endsWith("s")) return [word.slice(0, -1), word];
  if (word.endsWith("y") && !"aeiou".includes(word.at(-2) ?? "a")) return [word, word.slice(0, -1) + "ies"];
  return [word, word + "s"];
}

export function extractTerms(question: string): string[] {
  const tokens = question.toLowerCase().match(/[a-z0-9][a-z0-9-]*/g) ?? [];
  const terms: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i]!;
    const next = tokens[i + 1];
    if (KEEP_SUFFIX_AFTER.has(tok) && next && next.length <= 3) {
      terms.push(`"${tok} ${next}"`);
      i++;
      continue;
    }
    if (!STOPWORDS.has(tok) && tok.length > 1 && !/^\d+$/.test(tok)) terms.push(tok);
  }
  return [...new Set(terms)]; // sin duplicados, en orden
}

function termClause(term: string): string {
  if (term.startsWith('"')) return term;
  const v = variants(term);
  return v.length === 1 ? v[0]! : `(${v.join(" OR ")})`;
}

/** Un concepto de la pregunta y sus sinónimos aceptados (MeSH, Wikidata… filtrados con Jev). */
export interface ConceptGroup {
  concept: string;
  synonyms: string[];
  // Variantes automáticas del concepto que no se buscan por ambiguas ("cold" para "colds")
  exclude?: string[];
}

/** Todas las formas de un término: plurales si es una palabra; entre comillas si es una frase. */
export function termForms(term: string): string[] {
  const t = term.toLowerCase().replace(/"/g, "").trim();
  if (!t) return [];
  return /\s/.test(t) ? [`"${t}"`] : variants(t);
}

export function groupClause(g: ConceptGroup): string {
  const excluded = new Set(g.exclude ?? []);
  const forms = [...new Set([g.concept, ...g.synonyms].flatMap(termForms))].filter((f) => !excluded.has(f));
  return forms.length === 1 ? forms[0]! : `(${forms.join(" OR ")})`;
}

/** Consultas de más a menos estricta con grupos de sinónimos: todos, y luego quitando los últimos. */
export function queriesFromGroups(groups: ConceptGroup[]): string[] {
  if (!groups.length) throw new QueryError("No se han encontrado términos de búsqueda en la pregunta.");
  const clauses = groups.map(groupClause);
  const queries: string[] = [];
  for (let n = clauses.length; n >= Math.min(2, clauses.length); n--) queries.push(clauses.slice(0, n).join(" AND "));
  return queries;
}

export function termsToQuery(terms: string[]): string {
  return terms.map(termClause).join(" AND ");
}

/** Consultas de más a menos estricta: todos los términos, y luego quitando los últimos. */
export function candidateQueries(questionEn: string): string[] {
  const terms = extractTerms(questionEn);
  if (!terms.length) {
    throw new QueryError(
      "No se han encontrado términos de búsqueda en la pregunta. " +
        "Escríbelos en el campo «Términos de búsqueda».",
    );
  }
  const queries: string[] = [];
  for (let n = terms.length; n >= Math.min(2, terms.length); n--) {
    queries.push(termsToQuery(terms.slice(0, n)));
  }
  return queries;
}
